import { store } from "@/store/stores"
import Vue from "vue"
import userService from "@/services/userService"
import trainingFeatureAccess from "./trainingFeatureAccess"
import { buildTrainingLiveState, createTrainingSessionId } from "./trainingLiveState"
import { isWorkoutRestExpiredOnResume, isWorkoutStale, workoutElapsedSeconds, workoutRecoveryInfo, workoutReviewTiming, workoutStartedAt } from "./workoutRecovery"

class TrainingLiveService {
  constructor() {
    this.ready = false
    this.queue = Promise.resolve()
    this.pendingResumeSessionId = null
    this.bootstrapVersion = 0
    this.exerciseTimerChecks = new Set()
    this.viewState = Vue.observable({ watch: null, notificationsEnabled: null })
    this.handleState = (event) => {
      if (!trainingFeatureAccess.isAllowed()) { this.viewState.watch = null; return }
      const state = event?.detail
      if (!state || state.userId !== String(userService.getUserId() || "")) return
      if (state.owner !== "watch") { this.viewState.watch = null; return }
      const current = this.viewState.watch
      if (current?.clientSessionId === state.clientSessionId && current.revision > state.revision) return
      this.viewState.watch = state
    }
    this.handleReady = () => this.bootstrap()
    this.handleForeground = () => {
      this.clearExpiredRestOnResume()
      this.recoverOnForeground()
      return this.bootstrap({ foreground: true })
    }
    this.handleOpen = (event) => this.open(event?.detail?.clientSessionId, event?.detail?.action)
    this.handleVisible = () => { if (document.visibilityState === "visible") return this.handleForeground() }
  }

  async start(router) {
    if (this.unsubscribe) return
    this.router = router
    await store.restored
    this.unsubscribeAccess = trainingFeatureAccess.subscribe(() => this.bootstrap())
    this.unsubscribe = store.subscribe((mutation) => {
      if (mutation.type === "userStore/setUser") this.bootstrap()
      if (!mutation.type.startsWith("runWorkoutStore/") || mutation.type.endsWith("SET_LIVE_SESSION")) return
      if (this.ready && mutation.type.endsWith("RESET_WORKOUT_STATE") && this.lastPhoneSessionId && this.lastPhoneSessionId !== this.savedSessionId) {
        this.enqueue("watchCompanionWorkoutCommand", { action: "discard", clientSessionId: this.lastPhoneSessionId })
        this.lastPhoneSessionId = null
      }
      if (mutation.type.endsWith("SET_LIVE_DISPLAY_SETTINGS")) this.bootstrap()
      else this.schedule()
    })
    window.addEventListener("flutterInAppWebViewPlatformReady", this.handleReady)
    window.addEventListener("healthImportBridgeReady", this.handleReady)
    window.addEventListener("trainingLiveOpen", this.handleOpen)
    window.addEventListener("trainingLiveStateChanged", this.handleState)
    window.addEventListener("trainingLiveForeground", this.handleForeground)
    document.addEventListener("visibilitychange", this.handleVisible)
    await this.bootstrap()
  }

  stop() {
    this.bootstrapVersion++
    this.unsubscribe?.()
    this.unsubscribe = null
    this.unsubscribeAccess?.(); this.unsubscribeAccess = null
    this.ready = false
    this.pendingResumeSessionId = null
    window.removeEventListener("flutterInAppWebViewPlatformReady", this.handleReady)
    window.removeEventListener("healthImportBridgeReady", this.handleReady)
    window.removeEventListener("trainingLiveOpen", this.handleOpen)
    window.removeEventListener("trainingLiveStateChanged", this.handleState)
    window.removeEventListener("trainingLiveForeground", this.handleForeground)
    document.removeEventListener("visibilitychange", this.handleVisible)
  }

  async call(method, payload = {}) {
    const cleanup = method === "trainingLiveConfigure" && payload.userId === "" && payload.enabled === false
    const access = trainingFeatureAccess.capture()
    if (access === null && !cleanup) return { supported: false, accepted: false }
    const bridge = window.flutter_inappwebview
    if (!bridge?.callHandler) return null
    const userId = String(userService.getUserId() || "")
    const token = window.__wellcomeHealthImportToken
    if (payload.userId && payload.userId !== userId) return { supported: false, accepted: false }
    let timeout
    try {
      const response = await Promise.race([
        Promise.resolve(bridge.callHandler(method, { ...payload, userId: payload.userId ?? userId, bridgeToken: token })),
        new Promise((resolve) => { timeout = setTimeout(() => resolve({ supported: false, accepted: false }), method === "trainingLiveAuthorize" ? 300000 : 2500) }),
      ])
      return (cleanup || trainingFeatureAccess.isCurrent(access)) && userId === String(userService.getUserId() || "") && token === window.__wellcomeHealthImportToken
        ? response : { supported: false, accepted: false }
    } finally {
      clearTimeout(timeout)
    }
  }

  ensureSession() {
    const state = store.state.runWorkoutStore
    const userId = String(userService.getUserId() || "")
    if (!state.activeSplit || !userId) return null
    if (state.liveSession?.userId && state.liveSession.userId !== userId) return null
    if (!state.liveSession?.clientSessionId) {
      store.commit("runWorkoutStore/SET_LIVE_SESSION", {
        clientSessionId: state.watchCompanion?.clientSessionId || createTrainingSessionId(),
        userId, startedAt: Date.parse(state.workoutStartTime) || Date.now(), revision: 0, phase: "active", syncState: "local",
      })
    }
    if (state.liveSession.owner === "watch") { this.lastPhoneSessionId = null; return state.liveSession }
    if (state.liveSession.savedAt) {
      this.savedSessionId = state.liveSession.clientSessionId
      this.lastPhoneSessionId = null
    } else this.lastPhoneSessionId = state.liveSession.clientSessionId
    return state.liveSession
  }

  async bootstrap({ foreground = document.visibilityState === "visible" } = {}) {
    if (!trainingFeatureAccess.isAllowed()) {
      this.bootstrapVersion++
      this.ready = false; this.viewState.watch = null; this.viewState.notificationsEnabled = null
      this.pendingResumeSessionId = null; this.lastSignature = null
      if (trainingFeatureAccess.state.ready) await this.call("trainingLiveConfigure", { userId: "", enabled: false, showHeartRate: false, showEnergy: false })
      return
    }
    // Optional adapters advertise support; older wrappers keep the normal workout.
    if (!window.flutter_inappwebview?.callHandler) return
    const access = trainingFeatureAccess.capture()
    const version = ++this.bootstrapVersion
    try {
      const userId = String(userService.getUserId() || "")
      const settings = store.state.runWorkoutStore.liveDisplaySettings || {}
      const response = await this.call("trainingLiveConfigure", {
        userId, enabled: settings.enabled !== false,
        showHeartRate: settings.showHeartRate === true, showEnergy: settings.showEnergy === true,
      })
      if (version !== this.bootstrapVersion || !trainingFeatureAccess.isCurrent(access)) return
      this.ready = response?.supported === true
      this.viewState.notificationsEnabled = response?.notificationsEnabled ?? null
      if (!this.ready || !trainingFeatureAccess.isAllowed()) return
      if (response.pendingHealthOpen) window.dispatchEvent(new Event("healthConnectOpen"))
      this.lastSignature = null
      const live = this.ensureSession()
      if (response.snapshot) this.handleState({ detail: response.snapshot })
      if (live?.owner === "watch") { this.pendingResumeSessionId = null; return }
      if (live && response.snapshot?.clientSessionId === live.clientSessionId) {
        store.commit("runWorkoutStore/SET_LIVE_SESSION", { revision: Math.max(live.revision || 0, response.snapshot.revision || 0) })
      }
      if (foreground && this.router?.currentRoute?.path === "/workout/run/split" && live?.phase !== "ending"
        && store.state.runWorkoutStore.liveDisplaySettings?.enabled !== false) {
        this.pendingResumeSessionId = live?.clientSessionId || null
      }
      await this.call("trainingLiveReconcile", { clientSessionId: live?.clientSessionId || null })
      this.schedule()
      if (response.pendingOpen) await this.open(response.pendingOpen, response.pendingOpenAction)
    } catch (_) {
      // Optional system display never blocks the workout or an older wrapper.
      if (version === this.bootstrapVersion) this.ready = false
    }
  }

  schedule() {
    // Coalesce a synchronous Vuex action (set + superset advance) after commits.
    if (this.scheduled) return
    this.scheduled = true
    Promise.resolve().then(() => {
      this.scheduled = false
      const live = this.ensureSession()
      if (!this.ready || !trainingFeatureAccess.isAllowed()) return
      if (live?.owner === "watch") return
      const payload = live && buildTrainingLiveState(store.state.runWorkoutStore, userService.getUserId())
      if (!payload) {
        this.pendingResumeSessionId = null
        this.enqueue("trainingLiveReconcile", { clientSessionId: null })
        return
      }
      const timeBasis = payload.phase === "ending" ? payload.elapsedSeconds : Math.round(payload.elapsedAnchorAt - payload.elapsedSeconds * 1000)
      const signature = JSON.stringify({ ...payload, revision: 0, updatedAt: 0, elapsedSeconds: timeBasis, elapsedAnchorAt: 0 })
      const resumeActivity = this.pendingResumeSessionId === payload.clientSessionId && payload.phase !== "ending"
        && store.state.runWorkoutStore.liveDisplaySettings?.enabled !== false
      this.pendingResumeSessionId = null
      if (signature === this.lastSignature && !resumeActivity) return
      this.lastSignature = signature
      payload.revision = (live.revision || 0) + 1
      store.commit("runWorkoutStore/SET_LIVE_SESSION", { revision: payload.revision })
      if (this.lastPublishedSessionId !== payload.clientSessionId) {
        this.enqueue("trainingLiveReconcile", { clientSessionId: payload.clientSessionId })
        this.lastPublishedSessionId = payload.clientSessionId
      }
      // One foreground recovery request, attached to the latest state. It must
      // neither become part of the saved workout nor retrigger on every timer tick.
      this.enqueue("trainingLiveUpdate", resumeActivity ? { ...payload, resumeActivity: true } : payload)
    })
  }

  enqueue(method, payload) {
    const access = trainingFeatureAccess.capture()
    this.queue = this.queue.then(async () => {
      if (!(method === "trainingLiveConfigure" && payload.userId === "" && payload.enabled === false) && !trainingFeatureAccess.isCurrent(access)) return
      const result = await this.call(method, payload)
      if (result?.accepted === false) this.lastSignature = null
    }).catch(() => { this.lastSignature = null })
    return this.queue
  }

  ending() {
    const live = this.ensureSession()
    if (!live || live.owner === "watch" || live.phase === "ending") return
    if (isWorkoutStale(store.state.runWorkoutStore)) return this.prepareRecovery()
    store.commit("runWorkoutStore/SET_LIVE_SESSION", { phase: "ending", endedAt: Date.now(), elapsedSeconds: workoutElapsedSeconds(store.state.runWorkoutStore), syncState: "pending" })
    this.lastSignature = null
    this.schedule()
  }

  recoveryInfo(now = Date.now()) {
    this.ensureSession()
    return workoutRecoveryInfo(store.state.runWorkoutStore, userService.getUserId(), now)
  }

  registerExerciseTimer(checkRunning) {
    // Local stopwatches are intentionally not persisted across app restarts.
    this.exerciseTimerChecks.add(checkRunning)
    return () => this.exerciseTimerChecks.delete(checkRunning)
  }

  recoverOnForeground() {
    if (this.router?.currentRoute?.path !== "/workout/run/split") return
    if ([...this.exerciseTimerChecks].some((check) => check())) return
    const destination = this.entryRoute()
    if (destination) this.router.replace(destination).catch(() => {})
  }

  entryRoute() {
    const live = this.ensureSession()
    if (!live) return { path: "/workout/hub", query: { mode: "own" } }
    if (["ending", "completed", "discarded"].includes(live.phase) || live.pendingSave || live.savedAt) return { path: "/workout/summary" }
    if (isWorkoutStale(store.state.runWorkoutStore)) return { path: "/workout/hub", query: { mode: "own", recover: live.clientSessionId } }
    return null
  }

  requestRecovery(force = false) {
    this.ensureSession()
    const info = workoutRecoveryInfo(store.state.runWorkoutStore, userService.getUserId(), Date.now(), force)
    if (!info) return false
    this.router?.push({ path: "/workout/hub", query: { mode: "own", recover: info.clientSessionId } }).catch(() => {})
    return true
  }

  prepareRecovery() {
    const live = this.ensureSession()
    if (!live || live.owner === "watch" || live.savedAt || live.pendingSave) return
    const timing = workoutReviewTiming(store.state.runWorkoutStore)
    store.commit("runWorkoutStore/SET_LIVE_SESSION", {
      phase: "ending", endedAt: timing.endedAt, elapsedSeconds: timing.durationSeconds || 0,
      timeReviewRequired: true, recoveryReview: true, syncState: "pending",
    })
    this.schedule()
  }

  confirmTiming({ startedAt, endedAt, durationSeconds }) {
    const live = this.ensureSession()
    if (!live || live.savedAt || live.pendingSave || !Number.isFinite(startedAt) || !Number.isFinite(endedAt)
      || startedAt > endedAt || endedAt > Date.now() + 1000 || !Number.isFinite(durationSeconds) || durationSeconds <= 0 || durationSeconds > 86400
      || durationSeconds > (endedAt - startedAt) / 1000 + 1) return false
    store.commit("runWorkoutStore/SET_LIVE_SESSION", {
      startedAt, endedAt, elapsedSeconds: durationSeconds, phase: "ending", timeReviewRequired: false,
      pausedSeconds: Math.max(0, (endedAt - startedAt) / 1000 - durationSeconds),
    })
    this.schedule()
    return true
  }

  resumeRecovered() {
    const live = this.ensureSession()
    if (!live || live.savedAt || live.pendingSave) return false
    if (live.phase !== "ending" && !live.timeReviewRequired && !isWorkoutStale(store.state.runWorkoutStore)) {
      this.resume()
      return true
    }
    const timing = workoutReviewTiming(store.state.runWorkoutStore)
    if (timing.durationSeconds === null || live.timeReviewRequired) return false
    this.resumeFromElapsed(timing.durationSeconds)
    return true
  }

  resumeFromElapsed(elapsedSeconds) {
    const now = Date.now()
    const startedAt = workoutStartedAt(store.state.runWorkoutStore)
    store.commit("runWorkoutStore/SET_LIVE_SESSION", {
      phase: "active", endedAt: null, elapsedSeconds: null, lastActivityAt: now,
      pausedSeconds: Math.max(0, (now - startedAt) / 1000 - elapsedSeconds),
      timeReviewRequired: false, recoveryReview: false, syncState: "local",
    })
    store.commit("runWorkoutStore/STOP_TIMER")
    store.commit("runWorkoutStore/SET_LIVE_FEEDBACK_REST", null)
    this.schedule()
  }

  async saved() {
    const live = this.ensureSession()
    // Mark success before reset can run, even when the native bridge is delayed.
    this.savedSessionId = live?.clientSessionId
    if (live) store.commit("runWorkoutStore/SET_LIVE_SESSION", { savedAt: Date.now(), syncState: "saved", pendingSave: null })
    this.lastPhoneSessionId = null
    if (live && this.ready) await this.enqueue("trainingLiveSaved", { clientSessionId: live.clientSessionId })
  }

  clearExpiredRestOnResume() {
    const { timerState: timer, liveFeedbackRest: feedback } = store.state.runWorkoutStore
    const now = Date.now()
    if (timer?.isActive && isWorkoutRestExpiredOnResume(timer.startTime, timer.duration, now)) {
      store.commit("runWorkoutStore/STOP_TIMER")
    }
    if (feedback && isWorkoutRestExpiredOnResume(feedback.startedAt, (feedback.endsAt - feedback.startedAt) / 1000, now)) {
      store.commit("runWorkoutStore/SET_LIVE_FEEDBACK_REST", null)
    }
  }

  resume() {
    this.clearExpiredRestOnResume()
    const live = this.ensureSession()
    if (!live || live.owner === "watch" || live.savedAt || live.pendingSave || live.timeReviewRequired || isWorkoutStale(store.state.runWorkoutStore)) return
    if (live.phase === "ending") this.resumeFromElapsed(live.elapsedSeconds)
    this.pendingResumeSessionId = live.clientSessionId
    this.lastSignature = null
    if (this.ready) this.schedule()
    else this.bootstrap()
  }

  async clear() {
    this.bootstrapVersion++
    this.ready = false
    this.viewState.watch = null
    this.lastSignature = null
    this.pendingResumeSessionId = null
    store.commit("runWorkoutStore/RESET_WORKOUT_STATE")
    await this.enqueue("trainingLiveConfigure", { userId: "", enabled: false, showHeartRate: false, showEnergy: false })
  }

  async authorize() {
    if (!trainingFeatureAccess.isAllowed()) return false
    await this.bootstrap()
    const response = await this.call("trainingLiveAuthorize")
    this.viewState.notificationsEnabled = response?.granted === true
    this.lastSignature = null
    this.schedule()
    return response?.granted === true
  }

  async open(sessionId, action) {
    if (!trainingFeatureAccess.isAllowed() || !sessionId || !userService.getUserId() || !this.router) return
    const live = this.ensureSession()
    // A shared session opens the normal editor with its existing identity.
    const path = live?.clientSessionId === sessionId
      ? live.phase === "ending" ? "/workout/summary" : "/workout/run/split"
      : "/workout/hub"
    if (path === "/workout/run/split" && action === "enter-set") {
      const query = this.router.currentRoute.path === path ? this.router.currentRoute.query || {} : {}
      await this.router.push({ path, query: { ...query, liveSet: sessionId } }).catch(() => {})
    } else if (this.router.currentRoute.path !== path) await this.router.push(path).catch(() => {})
    if (path === "/workout/hub") {
      await Vue.nextTick()
      document.getElementById("watch-live-training")?.scrollIntoView({ block: "center" })
    }
    await this.call("trainingLiveOpenHandled", { clientSessionId: sessionId })
  }
}

export default new TrainingLiveService()
